import {
	BRAND_ACCENT,
	FORM_CONTROL_BORDER,
	FORM_CONTROL_BORDER_RADIUS,
	FORM_CONTROL_HEIGHT,
	FORM_CONTROL_PADDING,
	FORM_INPUT_FONT_SIZE,
} from "@constants/brand.colors";
import type { InputFieldSize } from "../types/inputfield";

const sizeMap = {
	small: { height: "32px", padding: "6px 10px" },
	medium: { height: FORM_CONTROL_HEIGHT, padding: FORM_CONTROL_PADDING },
	large: { height: FORM_CONTROL_HEIGHT, padding: FORM_CONTROL_PADDING },
};

export const TextFieldStyles = (isClickable: boolean | undefined, size: InputFieldSize = "medium", error?: boolean) => {
	let height = FORM_CONTROL_HEIGHT;
	let padding = FORM_CONTROL_PADDING;

	if (typeof size === "string" && sizeMap[size]) {
		height = sizeMap[size].height;
		padding = sizeMap[size].padding;
	} else if (typeof size === "object" && size !== null) {
		height = size.height || height;
		padding = size.padding || padding;
	}

	const borderColor = error ? "#d32f2f !important" : `${FORM_CONTROL_BORDER} !important`;
	const activeBorderColor = error ? "#d32f2f !important" : `${BRAND_ACCENT} !important`;

	return {
		background: "transparent",
		cursor: isClickable ? "pointer" : "text",

		"&.Mui-disabled": {
			opacity: 1,
			cursor: "not-allowed",
		},

		"& .MuiOutlinedInput-root.Mui-disabled": {
			backgroundColor: "#f3f4f6",
		},

		"& .MuiOutlinedInput-root.Mui-disabled fieldset": {
			borderColor: "#e5e7eb !important",
		},

		"& textarea": {
			fontFamily: "Poppins, sans-serif",
			fontSize: FORM_INPUT_FONT_SIZE,
			fontWeight: "400",
			"&::placeholder": {
				color: "#9ca3af",
				opacity: 1,
				fontSize: FORM_INPUT_FONT_SIZE,
			},
		},

		"& input": {
			boxSizing: "border-box",
			color: "#4b5563",
			cursor: isClickable ? "pointer" : "text",
			borderRadius: FORM_CONTROL_BORDER_RADIUS,
			padding,
			minHeight: "unset",
			height,
			fontFamily: "Poppins, sans-serif",
			paddingLeft: "10px",
			fontSize: FORM_INPUT_FONT_SIZE,
			fontWeight: "400",
			"&.Mui-disabled": {
				WebkitTextFillColor: "#6B7280",
				color: "#6B7280",
				opacity: 1,
				cursor: "not-allowed",
			},
			"&::placeholder": {
				color: "#9ca3af",
				opacity: 1,
			},
		},

		"& input[type='date']": {
			color: "#000",
			fontSize: FORM_INPUT_FONT_SIZE,
			fontWeight: "400",
			height: `${height} !important`,
			paddingRight: "40px",
			boxSizing: "border-box",
			"&.Mui-disabled": {
				WebkitTextFillColor: "#6B7280",
				color: "#6B7280",
				opacity: 1,
				cursor: "not-allowed",
			},
			"&:invalid": {
				color: "#6B7280",
			},
			"&::-webkit-datetime-edit": {
				color: "#9ca3af",
			},
			"&:valid::-webkit-datetime-edit": {
				color: "#4b5563",
			},
			"&::-webkit-calendar-picker-indicator": {
				opacity: 0.6,
				cursor: "pointer",
				position: "absolute",
				right: "12px",
			},
		},

		"& .MuiInputBase-root": {
			position: "relative",
		},

		"& fieldset": {
			borderColor,
			borderWidth: 1,
			borderRadius: FORM_CONTROL_BORDER_RADIUS,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderRadius: FORM_CONTROL_BORDER_RADIUS,
		},
		"&:hover fieldset": {
			borderColor: activeBorderColor,
		},
		"&.Mui-focused fieldset": {
			borderColor: activeBorderColor,
			borderWidth: "1px !important",
		},
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: activeBorderColor,
			borderWidth: "1px !important",
		},

		"&.Mui-error fieldset, &.Mui-error .MuiOutlinedInput-notchedOutline": {
			borderColor: "#d32f2f !important",
		},
		"&.Mui-error:hover fieldset, &.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "#d32f2f !important",
		},
		"&.Mui-error.Mui-focused fieldset, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#d32f2f !important",
			borderWidth: "1px !important",
		},

		"& .MuiInputAdornment-root svg": {
			color: "#8b8b8b",
		},
		"&.Mui-focused .MuiInputAdornment-root svg": {
			color: "#8b8b8b",
		},
		"&.Mui-error .MuiInputAdornment-root svg": {
			color: "#d32f2f",
		},
		"& .MuiInputAdornment-positionStart": {
			marginRight: "0px",
		},
		"& .MuiInputAdornment-positionEnd": {
			marginLeft: "0px",
		},

		"& .MuiSelect-select": {
			padding: `${padding} !important`,
			height: `${height} !important`,
			minHeight: `${height} !important`,
			display: "flex",
			alignItems: "center",
			fontFamily: "Poppins, sans-serif",
			fontSize: FORM_INPUT_FONT_SIZE,
			borderRadius: FORM_CONTROL_BORDER_RADIUS,
			boxSizing: "border-box",
			color: "#4b5563",
			fontWeight: "400",
			"&.MuiSelect-select": {
				color: "#4b5563",
				fontWeight: "400",
			},
			"&.Mui-disabled": {
				WebkitTextFillColor: "#6B7280",
				color: "#6B7280",
				opacity: 1,
				cursor: "not-allowed",
			},
		},

		"& .MuiOutlinedInput-root": {
			minHeight: height,
			boxSizing: "border-box",
			borderRadius: FORM_CONTROL_BORDER_RADIUS,
			overflow: "hidden",
			backgroundColor: "#ffffff",
		},
		"& .MuiInputBase-input::placeholder": {
			color: "#9ca3af",
			opacity: 1,
			fontSize: FORM_INPUT_FONT_SIZE,
		},

		"& .MuiFormHelperText-root": {
			marginTop: "2px",
			minHeight: "14px",
			fontSize: "11px",
			marginLeft: "0px",
			fontFamily: "Poppins, sans-serif",
			backgroundColor: "transparent",
			color: error ? "#d32f2f" : "transparent",
			lineHeight: "14px",
		},
		"& .MuiFormHelperText-root.Mui-error": {
			color: "#d32f2f",
		},
	};
};
